import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Star,
  Shield,
  Truck,
  Clock,
  MapPin,
  PhoneCall,
  BadgeCheck,
  Package,
} from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import ChatWidget from "../components/site/ChatWidget";
import QuoteEstimator from "../components/site/QuoteEstimator";
import { CONTACT, SERVICES } from "../lib/api";

const testimonials = [
  {
    name: "Anjali R.",
    city: "Hyderabad → Bengaluru",
    text:
      "Our 3BHK move was flawless. Everything arrived without a scratch, and the ops manager kept me updated every few hours.",
  },
  {
    name: "Vikram S.",
    city: "Secunderabad → Chennai",
    text:
      "Great pricing and honest people. They dismantled our wooden bed & sofa carefully and reassembled at the new home.",
  },
  {
    name: "Meera K.",
    city: "Hyderabad (local)",
    text:
      "Booked at 9pm, moved by 8am next day. The team was polite, uniformed and fast. Would recommend to anyone in the city.",
  },
  {
    name: "Ravi P.",
    city: "Office move — Hitech City",
    text:
      "We shifted 40 workstations over a weekend. Zero downtime on Monday morning. Absolutely worth the price.",
  },
];

const trustBadges = [
  "Since 2016",
  "4.7★ · 226 Google reviews",
  "Insured transit up to ₹5L",
  "GPS-tracked vehicles",
  "Verified crew",
  "Doorstep unpacking",
  "48h reschedule window",
  "No hidden charges",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid gap-12 lg:grid-cols-[1.15fr_1fr] items-center relative z-10">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-border bg-muted/40 text-xs tracking-wide"
              data-testid="hero-badge"
            >
              <Star className="w-3.5 h-3.5 fill-primary text-primary" />
              <span className="font-medium">{CONTACT.rating}</span>
              <span className="text-muted-foreground">· {CONTACT.reviews} Google reviews · Hyderabad since {CONTACT.since}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              data-testid="hero-title"
            >
              Moving day, <em className="text-primary not-italic">handled with care.</em>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed"
            >
              Premium packers & movers in Hyderabad. Custom crates, insured transit, GPS-tracked
              trucks and a dedicated ops manager — from your first box to the final unpacking.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/quote">
                <Button
                  data-testid="hero-primary-cta"
                  className="h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-7 text-base"
                >
                  Get instant quote <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href={`tel:${CONTACT.phoneRaw}`}>
                <Button
                  variant="outline"
                  data-testid="hero-secondary-cta"
                  className="h-12 rounded-full border-foreground/20 hover:bg-muted px-6 text-base"
                >
                  <PhoneCall className="mr-2 w-4 h-4" /> {CONTACT.phone}
                </Button>
              </a>
            </motion.div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              <Stat n="9,800+" label="Homes moved" />
              <Stat n="4.7★" label="Google rating" />
              <Stat n="24/7" label="Ops support" />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-muted relative shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1592805144716-feeccccef5ac"
                alt="Express Logistics move team"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141110]/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-background/85 backdrop-blur-xl border border-white/40 p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Now serving</div>
                <div className="font-display text-lg mt-1 leading-tight">
                  Hyderabad · Bengaluru · Chennai · Mumbai · Delhi
                </div>
              </div>
            </div>
            <div className="absolute -top-5 -left-5 w-24 h-24 rounded-full bg-primary/10 blur-2xl" />
            <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-full bg-primary/20 blur-3xl" />
          </motion.div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="border-y border-border bg-muted/30 py-6 overflow-hidden">
        <div className="marquee-track flex gap-10 whitespace-nowrap will-change-transform">
          {[...trustBadges, ...trustBadges].map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
              <BadgeCheck className="w-4 h-4 text-primary" /> {b}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 lg:py-32" id="services">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary mb-3">
              Our services
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Everything you need — from a single box to a full home.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Every service is delivered by our own trained crew (never freelanced) and covered by transit insurance.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`service-card-${i}`}
                className="group rounded-2xl overflow-hidden bg-background border border-border hover:border-primary/40 hover:shadow-xl transition-[box-shadow,border-color] duration-300"
              >
                <div className="aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display text-xl">{s.title}</h3>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">
                      {s.priceLabel}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.blurb}</p>
                  <Link
                    to="/quote"
                    data-testid={`service-cta-${i}`}
                    className="mt-4 inline-flex items-center gap-1 text-sm text-primary font-medium"
                  >
                    Get quote <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 bg-muted/30" id="why">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid gap-16 lg:grid-cols-[1fr_1.2fr] items-center">
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1581573950452-5a438c5f390f"
                alt="Family relocating"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden md:block absolute -bottom-8 -right-8 w-56 rounded-2xl bg-background border border-border p-5 shadow-2xl">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Team ratio</div>
              <div className="font-display text-3xl">1 : 6</div>
              <div className="text-xs text-muted-foreground mt-1">Ops manager per 6 moves — never overbooked.</div>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary mb-3">Why families choose us</div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              A move worth <em className="text-primary not-italic">remembering</em> — for the right reasons.
            </h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {[
                { icon: Shield, t: "Insured transit", d: "Every consignment covered up to ₹5,00,000 by default." },
                { icon: Truck, t: "Own fleet", d: "12+ GPS-tracked trucks and closed containers — not middlemen." },
                { icon: Package, t: "Custom crating", d: "Bubble wrap, cornerboards & wooden crates for fragile items." },
                { icon: Clock, t: "On-time promise", d: "Late arrivals compensated ₹500/hour after the promised slot." },
                { icon: MapPin, t: "Pan-India", d: "Hyderabad HQ with reach to Bengaluru, Chennai, Mumbai & Delhi." },
                { icon: BadgeCheck, t: "No hidden fees", d: "Line-item quote sent by SMS. What you see is what you pay." },
              ].map((f, i) => (
                <div key={i} className="flex gap-4" data-testid={`why-item-${i}`}>
                  <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-primary/10 text-primary grid place-items-center">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium">{f.t}</div>
                    <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE ESTIMATOR */}
      <section className="py-24 lg:py-32" id="quote">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary mb-3">Instant AI estimate</div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              A real price band — not a placeholder.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Tell us about your move, our AI reviews route, home size, floors and season to produce a realistic estimate. A human ops manager confirms the final quote after a quick call.
            </p>
          </div>
          <QuoteEstimator />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary mb-3">
              What our families say
            </div>
            <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              226 reviews. 4.7 stars. One promise kept.
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="rounded-2xl bg-background border border-border p-6 flex flex-col"
              >
                <div className="flex gap-0.5 text-primary mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed flex-1">“{t.text}”</p>
                <div className="mt-5">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.city}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-3xl overflow-hidden bg-[#141110] text-[#F0EBE1] p-10 lg:p-16">
            <div className="grain-overlay" />
            <div className="relative z-10 grid gap-8 lg:grid-cols-[1.4fr_1fr] items-center">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25B48] mb-4">
                  Ready when you are
                </div>
                <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                  Talk to a real human in under 3 minutes.
                </h2>
                <p className="mt-5 text-white/70 leading-relaxed max-w-xl">
                  Our Hyderabad team is available every day, 8 AM to 9 PM. Call or WhatsApp us and we&apos;ll block your slot in the same call.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <a href={`tel:${CONTACT.phoneRaw}`}>
                  <Button
                    data-testid="cta-strip-call"
                    className="w-full h-12 rounded-full bg-[#C25B48] hover:bg-[#C25B48]/90 text-white text-base"
                  >
                    <PhoneCall className="mr-2 w-4 h-4" /> Call {CONTACT.phone}
                  </Button>
                </a>
                <Link to="/quote" className="w-full">
                  <Button
                    data-testid="cta-strip-quote"
                    variant="outline"
                    className="w-full h-12 rounded-full border-white/20 bg-transparent hover:bg-white/10 text-white text-base"
                  >
                    Get instant AI quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display text-3xl leading-none">{n}</div>
      <div className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}
